import type { Dispatch, RefObject, SetStateAction } from 'react'
import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { AttachmentsRefInterface } from 'modules/attachments/components/context'
import { useAddComment } from 'modules/comments/api/add-comment'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { useCommentsContext } from 'modules/comments/components/comments-list'
import type { OnErrorType } from 'modules/comments/types'
import {
  isCommunityExtra,
  isLightLectureExtra,
} from 'modules/comments/utils/comments-extra-type-guards'
import { communityKeys } from 'modules/community/api/key-factory'
import type { MentionInterface } from 'modules/community/components/editor/elements/mention/mention.types'
import type { PostAttachmentType } from 'modules/community/types/post-attachment'

interface UseCommentBoxResult {
  handleAddComment: (textHtml: string) => void
  attachmentsRef: RefObject<AttachmentsRefInterface>
  attachments: PostAttachmentType[]
  setAttachments: Dispatch<SetStateAction<PostAttachmentType[]>>
  initialHtml: string | undefined
  initialMentions: MentionInterface[]
}

export function useCommentBox({
  parentId,
  level = 0,
  setActiveComment = () => {},
  setOpenedResponses = () => {},
  onError,
  replyInfo,
  onCommentAdded,
}: {
  parentId: number | null
  level?: number
  setActiveComment?: (comment: CommentInterface | null) => void
  setOpenedResponses?: (value: boolean) => void
  onError?: OnErrorType
  replyInfo?: { userId: number; displayName: string; profileImage: string }
  onCommentAdded?: (commentId: number) => void
}): UseCommentBoxResult {
  const { extra, commentsType, resourceId } = useCommentsContext()
  const queryClient = useQueryClient()
  const parentIRI = parentId ? `/api/comment/comments/${parentId}` : null
  const attachmentsRef = useRef<AttachmentsRefInterface>(null)
  const [attachments, setAttachments] = useState<PostAttachmentType[]>([])

  const isCommunityComments = isCommunityExtra(extra)
  const isLightLecture = isLightLectureExtra(extra)

  const initialHtml: string | undefined = replyInfo
    ? `<p><@${replyInfo.userId}:${replyInfo.displayName}>, </p>`
    : undefined
  const initialMentions: MentionInterface[] = replyInfo
    ? [
        {
          userId: replyInfo.userId,
          displayName: replyInfo.displayName,
          profileImage: replyInfo.profileImage,
          positionStart: 0,
          positionEnd: 0,
        },
      ]
    : []

  const addCommentMutation = useAddComment({
    commentsType,
    pageId: resourceId,
    onSuccess: (data: CommentInterface) => {
      if (parentId !== null && level === 0) {
        setOpenedResponses(true)
      }
      setActiveComment(null)
      attachmentsRef.current?.clear()
      if (isCommunityComments) {
        onCommentAdded?.(data.id)
        void queryClient.invalidateQueries({
          queryKey: communityKeys.postsCommentsCount(extra.community.communityPath, [
            extra.community.postId,
          ]),
        })
        void queryClient.invalidateQueries({ queryKey: communityKeys.postsCommentsGeneral() })
      }
    },
    onError: (error: Error) => {
      onError?.(error)
    },
  })

  function handleAddComment(textHtml: string): void {
    const commentSource = isCommunityComments
      ? {
          commentSourceCommunityPost: {
            communityPostId: extra.community.postId,
          },
        }
      : isLightLecture
        ? {
            commentSourceLightLecture: {
              lectureId: extra.lightLecture.lectureId,
            },
          }
        : {
            commentSourcePage: {
              pageId: extra.course.pageId,
            },
          }
    const comment = {
      text: textHtml,
      parent: parentIRI,
      ...commentSource,
      attachments,
    }
    addCommentMutation.mutate(comment)
  }

  return {
    handleAddComment,
    attachmentsRef,
    attachments,
    setAttachments,
    initialHtml,
    initialMentions,
  }
}
